import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { API } from "../lib/api";

export default function VouchersSuccessPage() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("polling"); // polling | paid | error
    const [code, setCode] = useState("");
    const [tries, setTries] = useState(0);

    useEffect(() => {
        if (!sessionId) {
            setState("error");
            return;
        }
        let cancelled = false;
        async function poll() {
            try {
                const r = await fetch(
                    `${API}/api/vouchers/status/${sessionId}`
                );
                if (!r.ok) throw new Error("status fetch failed");
                const data = await r.json();
                if (cancelled) return;
                if (data.payment_status === "paid" && data.voucher_code) {
                    setCode(data.voucher_code);
                    setState("paid");
                } else if (tries > 15) {
                    setState("error");
                } else {
                    setTries((t) => t + 1);
                    setTimeout(poll, 2000);
                }
            } catch (e) {
                if (!cancelled) setState("error");
            }
        }
        poll();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    return (
        <section
            data-testid="vouchers-success"
            className="min-h-[85vh] bg-[#FAF8F4] pt-32 md:pt-40 pb-24 flex items-center"
        >
            <div className="mx-auto max-w-[720px] px-6 md:px-10 lg:px-14 w-full">
                {state === "polling" && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.4 }}
                        className="text-center"
                    >
                        <p className="overline mb-4">Processing payment</p>
                        <h1 className="font-display uppercase text-[clamp(2rem,5vw,3.5rem)] leading-[0.95] text-[#121212]">
                            One moment<span className="text-[#EB4A18]">…</span>
                        </h1>
                        <p className="mt-6 text-base text-[#6B6B6B]">
                            We're confirming your payment with Stripe. This
                            usually takes 3–8 seconds.
                        </p>
                    </motion.div>
                )}

                {state === "paid" && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
                    >
                        <p className="overline mb-3">Payment received ✓</p>
                        <h1 className="font-display uppercase text-[clamp(2.4rem,6vw,4.5rem)] leading-[0.9] text-[#121212]">
                            Thanks<span className="text-[#EB4A18]">.</span>
                        </h1>
                        <p className="mt-6 text-base md:text-lg text-[#3A3A3A]">
                            Your voucher is on its way by email. A confirmation
                            SMS has been sent to your mobile.
                        </p>
                        <p
                            data-testid="voucher-spam-notice"
                            className="mt-3 text-sm text-[#6B6B6B] italic"
                        >
                            Can't find the email? Check your spam or junk folder — it sometimes lands there the first time.
                        </p>
                        <div className="mt-10 p-8 bg-[#121212] text-[#FAF8F4]">
                            <p className="overline !text-[#EB4A18] mb-2">
                                Voucher code
                            </p>
                            <p
                                data-testid="voucher-code-display"
                                className="font-mono text-2xl md:text-3xl tracking-[0.15em] break-all"
                            >
                                {code}
                            </p>
                            <p className="mt-4 text-[0.65rem] uppercase tracking-[0.22em] text-[#FAF8F4]/50">
                                Keep this safe — present it at the counter.
                            </p>
                        </div>
                        <Link
                            to="/"
                            data-testid="voucher-home-btn"
                            className="btn-primary inline-flex mt-10 text-sm px-8 py-4"
                        >
                            Back to home
                            <span aria-hidden="true">→</span>
                        </Link>
                        <div className="mt-8 pt-6 border-t border-black/10">
                            <Link
                                to={`/vouchers/balance?code=${encodeURIComponent(code)}`}
                                data-testid="voucher-check-balance-link"
                                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#6B6B6B] hover:text-[#EB4A18] transition-colors"
                            >
                                Check this voucher's balance any time
                                <span aria-hidden="true">→</span>
                            </Link>
                        </div>
                    </motion.div>
                )}

                {state === "error" && (
                    <div className="text-center">
                        <p className="overline mb-3">Hmm</p>
                        <h1 className="font-display uppercase text-[clamp(2rem,5vw,3.5rem)] leading-[0.95] text-[#121212]">
                            Something's up<span className="text-[#EB4A18]">.</span>
                        </h1>
                        <p className="mt-6 text-base text-[#6B6B6B]">
                            We couldn't confirm your payment. If your card was
                            charged, the voucher will still be emailed to you
                            shortly — check your inbox in a few minutes.
                        </p>
                        <Link
                            to="/vouchers"
                            className="btn-primary inline-flex mt-10 text-sm px-8 py-4"
                        >
                            Back to vouchers
                            <span aria-hidden="true">→</span>
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
}
